/**
 * Manifesto de plugin: forma, vocabulário e as regras duras (SPEC-20260926-2036).
 *
 * Contrato completo em `docs/plugin-platform.md`. Isto aqui é a PORTA: o
 * manifesto é escrito por terceiro e é a única coisa que ele nos entrega antes
 * de existir para o Orbind. Tudo que passa daqui é tratado como cadastrado.
 *
 * Puro de propósito — sem rede, sem banco, sem `env`. É o que permite testar as
 * recusas que importam sem subir nada.
 */

/* ------------------------------------------------------------- escopos --- */

/**
 * O que um plugin pode pedir. Vocabulário fechado: escopo desconhecido no
 * manifesto é recusa, não "ignora e segue" — um plugin que pede o que não
 * existe está enganado sobre o que vai receber, e descobrir isso no cadastro é
 * melhor que descobrir em produção.
 *
 * A APLICAÇÃO dos escopos é da SPEC `plugin-autenticacao`; aqui só o
 * vocabulário, para servidor e telas falarem a mesma língua.
 */
export const PLUGIN_SCOPES = [
  'work-item:read',
  'work-item:write',
  'catalog:read',
  'member:read',
  'kanban:read',
  'kanban:write',
  'portfolio:read',
  'notification:write',
] as const
export type PluginScope = (typeof PLUGIN_SCOPES)[number]

/** Texto do Orbind, não do plugin: quem instala lê a NOSSA descrição. */
export const PLUGIN_SCOPE_LABEL: Record<PluginScope, string> = {
  'work-item:read': 'Ler Itens de Trabalho',
  'work-item:write': 'Criar e alterar Itens de Trabalho',
  'catalog:read': 'Ler catálogos (tipos, status, prioridades)',
  'member:read': 'Ler a lista de pessoas do workspace',
  'kanban:read': 'Ler quadros e a posição dos cards',
  'kanban:write': 'Mover cards nos quadros',
  'portfolio:read': 'Ler o portfólio',
  'notification:write': 'Enviar notificações às pessoas',
}

/** Escopo que ESCREVE — a tela separa os dois, porque o risco é diferente. */
export const escopoEscreve = (scope: PluginScope): boolean => scope.endsWith(':write')

/* ------------------------------------------------------------ eventos --- */

/**
 * Os eventos de domínio que um plugin pode assinar.
 *
 * Vocabulário fechado, pelo mesmo motivo dos escopos: um manifesto que assina
 * `work_item.deleted` está enganado sobre o que vai receber, e descobrir isso
 * no cadastro é melhor que descobrir nunca — porque o plugin simplesmente não
 * seria chamado, sem erro nenhum em lugar nenhum.
 *
 * O nome descreve o que JÁ aconteceu. Um evento no imperativo vira comando, e
 * comando não tem vários assinantes.
 */
export const PLUGIN_EVENTS = [
  'work_item.created',
  'work_item.assignee_changed',
  'work_item.status_changed',
  'work_item.checklist_completed',
] as const
export type PluginEvent = (typeof PLUGIN_EVENTS)[number]

/** Texto do Orbind: quem instala lê a NOSSA descrição do que o plugin vê. */
export const PLUGIN_EVENT_LABEL: Record<PluginEvent, string> = {
  'work_item.created': 'Um Item de Trabalho foi criado',
  'work_item.assignee_changed': 'O responsável por um Item mudou',
  'work_item.status_changed': 'O status de um Item mudou',
  'work_item.checklist_completed': 'Um checklist foi concluído',
}

/* ------------------------------------------------------- pontos de ext --- */

export const PLUGIN_EXTENSION_TYPES = [
  'rail-screen',
  'work-item-panel',
  'work-item-action',
  'card-badge',
  'event-listener',
] as const
export type PluginExtensionType = (typeof PLUGIN_EXTENSION_TYPES)[number]

export type TextoI18n = Readonly<Record<string, string>>

export type PluginExtension = {
  type: PluginExtensionType
  key: string
  label?: TextoI18n
  /** Caminho relativo à `baseUrl`. Obrigatório em tudo menos `card-badge`. */
  url?: string
  /** Só `card-badge`: o caminho que recebe o LOTE de ids. */
  endpoint?: string
  /** Só `card-badge`: por quanto tempo o cliente pode reusar a resposta. */
  ttlSeconds?: number
  /** Só `event-listener`. */
  events?: readonly string[]
  /** Só `rail-screen`: classe do Font Awesome. */
  icon?: string
}

export type PluginManifest = {
  key: string
  name: TextoI18n
  version: string
  baseUrl: string
  vendor?: { name?: string; url?: string }
  lifecycle: { installed: string; uninstalled?: string }
  scopes: readonly PluginScope[]
  extensions: readonly PluginExtension[]
}

/* -------------------------------------------------------- as recusas ----- */

export type ErroDeManifesto = { campo: string; problema: string }

export type ResultadoDaValidacao =
  | { ok: true; manifesto: PluginManifest }
  | { ok: false; erros: readonly ErroDeManifesto[] }

/** `wynk-erp` sim; `Wynk ERP`, `../x` e vazio não. A key vai dentro de um JWT. */
const KEY_VALIDA = /^[a-z0-9][a-z0-9-]{0,79}$/

/**
 * Hosts que nunca podem ser `baseUrl` nem servir manifesto.
 *
 * Buscar o manifesto é o Orbind fazendo um GET numa URL que um terceiro
 * escolheu: sem esta lista, o cadastro vira um leitor de rede interna (SSRF).
 * Cobre o literal; DNS que RESOLVE para endereço privado continua passando —
 * fechar isso é trabalho do lado que faz o fetch, e está anotado lá.
 */
function hostÉPrivado(host: string): boolean {
  const h = host.toLowerCase()
  if (h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.local') || h.endsWith('.internal')) return true
  if (h === '[::1]' || h === '::1') return true
  // 127.0.0.0/8, 10.0.0.0/8, 192.168.0.0/16, 169.254.0.0/16 (inclui o metadata
  // da nuvem em 169.254.169.254) e 172.16.0.0/12
  if (/^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h) || /^169\.254\./.test(h)) return true
  const m = /^172\.(\d{1,3})\./.exec(h)
  if (m && Number(m[1]) >= 16 && Number(m[1]) <= 31) return true
  return false
}

/** `https://a.b/c` -> `https://a.b`. Devolve null se nem URL é. */
export function origemDe(url: string): string | null {
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

/**
 * Um caminho de extensão precisa ser RELATIVO e ficar sob a `baseUrl`.
 *
 * `//evil.com/x` é o caso traiçoeiro: parece caminho, e o navegador lê como
 * URL absoluta com o protocolo atual. Aceitar isso deixaria o manifesto
 * apontar a UI para um terceiro que o instalador nunca viu.
 */
function caminhoRelativoVálido(v: unknown): boolean {
  if (typeof v !== 'string' || v === '') return false
  if (!v.startsWith('/')) return false
  if (v.startsWith('//')) return false
  if (v.includes('..')) return false
  return true
}

const CAMPOS_POR_TIPO: Record<PluginExtensionType, (e: PluginExtension) => string | null> = {
  'rail-screen': (e) => (caminhoRelativoVálido(e.url) ? null : 'precisa de `url` relativa'),
  'work-item-panel': (e) => (caminhoRelativoVálido(e.url) ? null : 'precisa de `url` relativa'),
  'work-item-action': (e) => (caminhoRelativoVálido(e.url) ? null : 'precisa de `url` relativa'),
  'card-badge': (e) => (caminhoRelativoVálido(e.endpoint) ? null : 'precisa de `endpoint` relativo'),
  'event-listener': (e) => {
    if (!caminhoRelativoVálido(e.url)) return 'precisa de `url` relativa'
    if (!Array.isArray(e.events) || e.events.length === 0) return 'precisa de `events` com ao menos um evento'
    // evento desconhecido é recusa, não "ignora": um plugin que assina o que
    // não existe nunca seria chamado, e sem erro em lugar nenhum
    const forasteiros = e.events.filter((x) => !(PLUGIN_EVENTS as readonly string[]).includes(x))
    if (forasteiros.length > 0) return `evento desconhecido: ${forasteiros.join(', ')}`
    return null
  },
}

/**
 * Valida o manifesto bruto. Junta TODOS os problemas em vez de parar no
 * primeiro: quem publica um plugin merece a lista inteira de uma vez.
 *
 * `origemDoOrbind` entra por parâmetro porque o core não conhece `env` — e é o
 * que permite testar a regra mais importante sem subir servidor.
 */
export function validarManifesto(
  bruto: unknown,
  opcoes: {
    origemDoOrbind: string
    /** Só em desenvolvimento: deixa apontar para localhost para escrever plugin. */
    permitirHostLocal?: boolean
  },
): ResultadoDaValidacao {
  const erros: ErroDeManifesto[] = []
  const põe = (campo: string, problema: string) => erros.push({ campo, problema })

  if (typeof bruto !== 'object' || bruto === null || Array.isArray(bruto)) {
    return { ok: false, erros: [{ campo: '(raiz)', problema: 'o manifesto precisa ser um objeto JSON' }] }
  }
  const m = bruto as Record<string, unknown>

  if (typeof m['key'] !== 'string' || !KEY_VALIDA.test(m['key'])) {
    põe('key', 'obrigatória: minúsculas, números e hífen, começando por letra ou número')
  }
  if (typeof m['version'] !== 'string' || m['version'].trim() === '') {
    põe('version', 'obrigatória')
  }
  if (typeof m['name'] !== 'object' || m['name'] === null || Object.keys(m['name']).length === 0) {
    põe('name', 'obrigatório: mapa `{ locale: nome }` com ao menos uma entrada')
  }

  /* --- baseUrl: a regra que sustenta a arquitetura inteira --- */
  const base = m['baseUrl']
  const origemDoPlugin = typeof base === 'string' ? origemDe(base) : null
  if (typeof base !== 'string' || origemDoPlugin === null) {
    põe('baseUrl', 'obrigatória e precisa ser uma URL absoluta')
  } else {
    const u = new URL(base)
    if (u.protocol !== 'https:' && !opcoes.permitirHostLocal) {
      põe('baseUrl', 'precisa ser https')
    }
    if (origemDoPlugin === origemDoOrbindNormalizada(opcoes.origemDoOrbind)) {
      põe(
        'baseUrl',
        'não pode ser a mesma origem do Orbind: o isolamento de código de terceiro é o do navegador, por origem',
      )
    }
    if (hostÉPrivado(u.hostname) && !opcoes.permitirHostLocal) {
      põe('baseUrl', 'não pode apontar para host privado ou loopback')
    }
  }

  /* --- ciclo de vida --- */
  const ciclo = m['lifecycle']
  if (typeof ciclo !== 'object' || ciclo === null) {
    põe('lifecycle', 'obrigatório: ao menos `installed`')
  } else {
    const c = ciclo as Record<string, unknown>
    if (!caminhoRelativoVálido(c['installed'])) põe('lifecycle.installed', 'precisa ser um caminho relativo')
    if (c['uninstalled'] !== undefined && !caminhoRelativoVálido(c['uninstalled'])) {
      põe('lifecycle.uninstalled', 'precisa ser um caminho relativo')
    }
  }

  /* --- escopos --- */
  const scopes = m['scopes']
  if (!Array.isArray(scopes)) {
    põe('scopes', 'obrigatório: lista (pode ser vazia)')
  } else {
    for (const s of scopes) {
      if (typeof s !== 'string' || !(PLUGIN_SCOPES as readonly string[]).includes(s)) {
        põe('scopes', `escopo desconhecido: ${String(s)}`)
      }
    }
  }

  /* --- extensões --- */
  const exts = m['extensions']
  if (!Array.isArray(exts)) {
    põe('extensions', 'obrigatório: lista (pode ser vazia)')
  } else {
    const vistas = new Set<string>()
    exts.forEach((bruta, i) => {
      const campo = `extensions[${i}]`
      if (typeof bruta !== 'object' || bruta === null) {
        põe(campo, 'precisa ser um objeto')
        return
      }
      const e = bruta as PluginExtension
      if (!(PLUGIN_EXTENSION_TYPES as readonly string[]).includes(e.type)) {
        põe(campo, `tipo desconhecido: ${String(e.type)}`)
        return
      }
      if (typeof e.key !== 'string' || !KEY_VALIDA.test(e.key)) {
        põe(`${campo}.key`, 'obrigatória: minúsculas, números e hífen')
      }
      const dupla = `${e.type}:${e.key}`
      if (vistas.has(dupla)) põe(campo, `repetida: já existe ${dupla}`)
      vistas.add(dupla)

      const problema = CAMPOS_POR_TIPO[e.type](e)
      if (problema) põe(campo, problema)
    })
  }

  if (erros.length > 0) return { ok: false, erros }
  return { ok: true, manifesto: bruto as PluginManifest }
}

/** Tolera barra no fim e caixa: `https://A.com/` e `https://a.com` são a mesma. */
function origemDoOrbindNormalizada(origem: string): string | null {
  return origemDe(origem)
}

/**
 * Monta a URL absoluta de uma extensão. Concentrada aqui para ninguém
 * concatenar string à mão e reabrir o buraco que `caminhoRelativoVálido` fecha.
 *
 * O caminho é APENDADO ao da `baseUrl`, não resolvido contra ela. A diferença
 * importa: `new URL('/telas/x', 'https://p.com/orbind')` devolve
 * `https://p.com/telas/x` — o subcaminho do plugin evapora, e a UI sai de
 * baixo da `baseUrl` que o instalador aceitou. Um plugin hospedado em
 * `https://host/a` poderia assim apontar para `https://host/b`.
 */
export function urlDaExtensao(baseUrl: string, caminho: string): string {
  const base = new URL(baseUrl)
  const prefixo = base.pathname.replace(/\/+$/, '')
  base.pathname = `${prefixo}${caminho}`
  return base.href
}
